import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

type Tribute = {
  id: string;
  slug: string;
  full_name: string;
  status: string;
  model_glb_url: string | null;
  interview_status: string | null;
};

type Job = {
  id: string;
  status: "pending" | "processing" | "ready" | "failed";
  photo_count: number;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
};

const POLL_INTERVAL_MS = 10000;

const ProcessingStatus = () => {
  const { slug } = useParams();
  const [tribute, setTribute] = useState<Tribute | null>(null);
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;

    const tick = async () => {
      const { data: trib } = await supabase
        .from("tributes")
        .select("id, slug, full_name, status, model_glb_url, interview_status")
        .eq("slug", slug)
        .single();

      if (cancelled) return;

      if (!trib) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setTribute(trib as Tribute);

      const { data: jobs } = await supabase
        .from("photogrammetry_jobs")
        .select("id, status, photo_count, started_at, finished_at, created_at")
        .eq("tribute_id", (trib as Tribute).id)
        .order("created_at", { ascending: false })
        .limit(1);

      if (cancelled) return;

      setJob(jobs && jobs.length > 0 ? (jobs[0] as Job) : null);
      setLoading(false);
    };

    tick();
    const interval = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (notFound || !tribute) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="font-display text-3xl font-bold mb-3">Tribute not found</h1>
          <Link to="/" className="text-primary hover:underline text-sm">
            ← Back to LegacyLens AI
          </Link>
        </div>
      </div>
    );
  }

  // Treat "ready" as derived from BOTH the tribute status flipping to ready
  // AND the model GLB being present, so we don't link to a tribute page that
  // can't render yet.
  const isReady = tribute.status === "ready" && tribute.model_glb_url;
  const isFailed = job?.status === "failed";

  // Whether the interview is still open for the subject to return to. ALLOWLIST
  // ONLY — every other value (null, unknown, interview_complete_pending_consent,
  // and all voice_training_* states) is "past returning" and must be false.
  const interviewOpen =
    tribute.interview_status === "awaiting_interview" ||
    tribute.interview_status === "interview_in_progress";

  return (
    <main className="min-h-screen bg-background">
      <div className="px-4 py-3 flex justify-center">
        <Link to="/" className="opacity-50 hover:opacity-80 transition-opacity">
          <img src="/ll-mark-256.png" alt="LegacyLens AI" className="h-7" />
        </Link>
      </div>

      <div className="container mx-auto max-w-2xl px-6 py-16 text-center">
        <h1 className="font-display text-3xl sm:text-4xl font-bold mb-3">
          {tribute.full_name}
        </h1>

        {isReady ? (
          <>
            <p className="text-muted-foreground mb-8">
              Your tribute is ready.
            </p>
            <Link
              to={`/tribute/${slug}`}
              className="inline-block rounded-md bg-primary px-6 py-3 text-primary-foreground hover:opacity-90 transition-opacity"
            >
              View tribute →
            </Link>
          </>
        ) : isFailed ? (
          <>
            <p className="text-sm text-destructive mb-6">
              Something went wrong processing your photos. Please contact support@legacylensai.com for help.
            </p>
            <Link to="/contact" className="text-primary hover:underline">
              Contact us to retry →
            </Link>
          </>
        ) : (
          <>
            <p className="text-lg text-foreground/90 mb-2">
              We're building your tribute.
            </p>
            <p className="text-muted-foreground mb-8">
              This usually takes about an hour — you don't need to wait here.
              We'll email you when it's ready.
            </p>
            <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
              </span>
              {job?.status === "processing" ? "Processing photos…" : "In queue…"}
              {job?.photo_count != null && ` • ${job.photo_count} photos`}
            </div>
          </>
        )}

        {/* Backup re-entry surface into the interview. Gated on interview_status
            (independent of model/photo state), so it renders in all three
            branches while the interview is still open and disappears once it is
            past returning. Same-tab /interview/:slug resumes from the stored
            session; the original email link is the recovery path if that's gone. */}
        {interviewOpen && (
          <div className="mt-10">
            <Link
              to={`/interview/${slug}`}
              className="inline-block rounded-md bg-primary px-6 py-3 text-primary-foreground hover:opacity-90 transition-opacity"
            >
              Continue to your interview questions →
            </Link>
          </div>
        )}
      </div>
    </main>
  );
};

export default ProcessingStatus;
